/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
  QueryKey,
} from "@tanstack/react-query";
import {
  BaseEntity,
  BaseService,
  QueryParams,
  PaginatedResponse,
} from "@/lib/api/base";
import { AxiosError, AxiosRequestConfig } from "axios";

// Enhanced Query Parameters interface
export interface EnhancedQueryParams extends QueryParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: "asc" | "desc";
  [key: string]: any;
}

// Define hook options interface
export interface QueryHookOptions {
  enableList?: boolean;
  enableDetail?: boolean;
  enableCreate?: boolean;
  enableUpdate?: boolean;
  enableDelete?: boolean;
  /**
   * When provided, entity detail cache keys include a scope derived from the
   * Axios request `config` (e.g. multi-tenant `garage_register_id` on GET/PATCH).
   */
  detailQueryScopeFromConfig?: (
    config?: AxiosRequestConfig
  ) => string | undefined;
}

// Default options with sensible defaults
const defaultHookOptions: QueryHookOptions = {
  enableList: true,
  enableDetail: true,
  enableCreate: true,
  enableUpdate: true,
  enableDelete: true,
};

// Optimistic update context for mutations
export interface OptimisticContext<T> {
  previousData: T | T[] | null;
  queryKey: QueryKey;
}

export function createQueryHooks<
  T extends BaseEntity,
  CreateDTO = Omit<T, "id">,
  UpdateDTO = Partial<CreateDTO>
>(
  entityName: string,
  service: BaseService<T, CreateDTO, UpdateDTO>,
  options: QueryHookOptions = defaultHookOptions
) {
  // Merge provided options with defaults (deep merge for nested objects)
  const hookOptions = {
    ...defaultHookOptions,
    ...options,
    // defaultQueryOptions: {
    //   ...defaultHookOptions.defaultQueryOptions,
    //   ...options.defaultQueryOptions,
    // },
  };

  // Helper to normalize entity IDs consistently
  const normalizeId = (id: string | number): string => String(id);

  const detailKey = (
    id: string | number,
    config?: AxiosRequestConfig
  ): QueryKey => {
    const parts: unknown[] = [entityName, "detail", normalizeId(id)];
    const scope = hookOptions.detailQueryScopeFromConfig?.(config);
    if (scope !== undefined && scope !== null && String(scope).trim() !== "") {
      parts.push(String(scope));
    }
    return parts as QueryKey;
  };

  // Enhanced query keys factory with better typing
  const keys = {
    all: [entityName] as const,
    lists: () => [...keys.all, "list"] as const,
    list: (filters: EnhancedQueryParams = {}) =>
      [...keys.lists(), filters] as const,
    details: () => [...keys.all, "detail"] as const,
    detail: (id: string | number, config?: AxiosRequestConfig) =>
      detailKey(id, config),
  };

  // Helper to update cache for lists
  const updateListCache = (
    queryClient: ReturnType<typeof useQueryClient>,
    updatedEntity: T
  ) => {
    const entityId = normalizeId(updatedEntity.id);

    queryClient.setQueriesData({ queryKey: keys.lists() }, (oldData: any) => {
      if (!oldData) return oldData;

      // Handle paginated response format
      if (oldData.data && Array.isArray(oldData.data)) {
        return {
          ...oldData,
          data: oldData.data.map((item: T) =>
            normalizeId(item.id) === entityId ? updatedEntity : item
          ),
        };
      }

      // Handle array response format
      if (Array.isArray(oldData)) {
        return oldData.map((item: T) =>
          normalizeId(item.id) === entityId ? updatedEntity : item
        );
      }

      return oldData;
    });
  };

  // Enhanced Query hooks with config support
  const useEntities = hookOptions.enableList
    ? <TData = T[]>(
        params: EnhancedQueryParams = {},
        options?: Partial<
          UseQueryOptions<PaginatedResponse<T> | T[], AxiosError, TData>
        >,
        config?: AxiosRequestConfig
      ) => {
        return useQuery<PaginatedResponse<T> | T[], AxiosError, TData>({
          queryKey: keys.list(params),
          queryFn: async () => await service.getAll(params, config),
          // ...hookOptions.defaultQueryOptions,
          ...options,
        });
      }
    : () => {
        throw new Error(
          `The 'useEntities' hook is disabled for '${entityName}' entity`
        );
      };

  const useEntity = hookOptions.enableDetail
    ? <TData = T>(
        id: string | number | null | undefined,
        config?: AxiosRequestConfig,
        options?: UseQueryOptions<T, AxiosError, TData>
      ) => {
        return useQuery<T, AxiosError, TData>({
          queryKey: keys.detail(id as string, config),
          queryFn: async () => await service.getById(id as string, config),
          enabled: !!id,
          // ...hookOptions.defaultQueryOptions,
          ...options,
        });
      }
    : () => {
        throw new Error(
          `The 'useEntity' hook is disabled for '${entityName}' entity`
        );
      };

  // Enhanced Mutation hooks with config support and optimistic updates
  const useCreateEntity = hookOptions.enableCreate
    ? (
        options?: UseMutationOptions<
          T,
          AxiosError,
          { data: CreateDTO; config?: AxiosRequestConfig },
          unknown
        >
      ) => {
        const queryClient = useQueryClient();
        const {
          onSuccess: userOnSuccess,
          onError: userOnError,
          onSettled: userOnSettled,
          ...restCreateOptions
        } = options ?? {};

        const mutation = useMutation<
          T,
          AxiosError,
          { data: CreateDTO; config?: AxiosRequestConfig },
          unknown
        >({
          ...restCreateOptions,
          mutationFn: async ({ data, config }) =>
            await service.create(data, config),
          onSuccess: (newEntity, variables, context) => {
            // Update detail cache
            queryClient.setQueryData(
              keys.detail(newEntity.id, variables.config),
              newEntity
            );

            // Invalidate list queries to refetch
            queryClient.invalidateQueries({
              queryKey: keys.lists(),
            });

            userOnSuccess?.(newEntity, variables, context);
          },
          onError: (error, variables, context) => {
            userOnError?.(error, variables, context);
          },
          onSettled: (data, error, variables, context) => {
            userOnSettled?.(data, error, variables, context);
          },
        });

        return {
          ...mutation,
          mutate: (data: CreateDTO, config?: AxiosRequestConfig) => {
            mutation.mutate({ data, config });
          },
          mutateAsync: (data: CreateDTO, config?: AxiosRequestConfig) => {
            return mutation.mutateAsync({ data, config });
          },
        };
      }
    : () => {
        throw new Error(
          `The 'useCreateEntity' hook is disabled for '${entityName}' entity`
        );
      };

  const useUpdateEntity = hookOptions.enableUpdate
    ? (
        options?: UseMutationOptions<
          T,
          AxiosError,
          { id: string | number; data: UpdateDTO; config?: AxiosRequestConfig },
          OptimisticContext<T>
        >
      ) => {
        const queryClient = useQueryClient();
        const {
          onSuccess: userOnSuccess,
          onError: userOnError,
          onSettled: userOnSettled,
          onMutate: userOnMutate,
          ...restUpdateOptions
        } = options ?? {};

        const mutation = useMutation<
          T,
          AxiosError,
          { id: string | number; data: UpdateDTO; config?: AxiosRequestConfig },
          OptimisticContext<T>
        >({
          ...restUpdateOptions,
          mutationFn: async ({ id, data, config }) =>
            await service.update(normalizeId(id), data, config),

          // Support for optimistic updates
          onMutate: async (variables) => {
            const userCtx =
              ((await userOnMutate?.(variables)) as
                | Record<string, unknown>
                | undefined) ?? {};
            const { id, config } = variables;
            const normalizedId = normalizeId(id);
            const scopedDetailKey = detailKey(normalizedId, config);

            // Cancel any outgoing refetches to avoid overwriting optimistic update
            await queryClient.cancelQueries({
              queryKey: scopedDetailKey,
            });

            // Snapshot the previous value
            const previousData =
              queryClient.getQueryData<T>(scopedDetailKey) || null;

            return {
              ...userCtx,
              previousData,
              queryKey: scopedDetailKey,
            };
          },

          onSuccess: (updatedEntity, variables, context) => {
            const normalizedId = normalizeId(variables.id);

            // Update the detail query
            queryClient.setQueryData(
              detailKey(normalizedId, variables.config),
              updatedEntity
            );

            // Update list caches
            updateListCache(queryClient, updatedEntity);

            userOnSuccess?.(updatedEntity, variables, context);
          },

          onError: (error, variables, context) => {
            // Revert to previous value on error if we have the optimistic snapshot
            if (context?.previousData && context.queryKey) {
              queryClient.setQueryData(context.queryKey, context.previousData);
            }

            userOnError?.(error, variables, context);
          },

          onSettled: (data, error, variables, context) => {
            // Always refetch to ensure server-client consistency
            queryClient.invalidateQueries({
              queryKey: detailKey(
                normalizeId(variables.id),
                variables.config
              ),
            });

            userOnSettled?.(data, error, variables, context);
          },
        });

        return {
          ...mutation,
          mutate: (
            id: string | number,
            data: UpdateDTO,
            config?: AxiosRequestConfig
          ) => {
            mutation.mutate({ id, data, config });
          },
          mutateAsync: (
            id: string | number,
            data: UpdateDTO,
            config?: AxiosRequestConfig
          ) => {
            return mutation.mutateAsync({ id, data, config });
          },
        };
      }
    : () => {
        throw new Error(
          `The 'useUpdateEntity' hook is disabled for '${entityName}' entity`
        );
      };

  const useDeleteEntity = hookOptions.enableDelete
    ? (
        options?: UseMutationOptions<
          void,
          AxiosError,
          { id: string | number; config?: AxiosRequestConfig },
          OptimisticContext<T>
        >
      ) => {
        const queryClient = useQueryClient();
        const {
          onSuccess: userOnSuccess,
          onError: userOnError,
          onSettled: userOnSettled,
          onMutate: userOnMutate,
          ...restDeleteOptions
        } = options ?? {};

        const mutation = useMutation<
          void,
          AxiosError,
          { id: string | number; config?: AxiosRequestConfig },
          OptimisticContext<T>
        >({
          ...restDeleteOptions,
          mutationFn: async ({ id, config }) =>
            await service.delete(normalizeId(id), config),

          // Support for optimistic updates
          onMutate: async (variables) => {
            const userCtx =
              ((await userOnMutate?.(variables)) as
                | Record<string, unknown>
                | undefined) ?? {};
            const { id, config } = variables;
            const normalizedId = normalizeId(id);
            const scopedDetailKey = detailKey(normalizedId, config);

            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
              queryKey: scopedDetailKey,
            });
            await queryClient.cancelQueries({
              queryKey: keys.lists(),
            });

            // Snapshot the previous entity data
            const previousData =
              queryClient.getQueryData<T>(scopedDetailKey) || null;

            // Remove the entity from lists optimistically
            queryClient.setQueriesData(
              { queryKey: keys.lists() },
              (oldData: any) => {
                if (!oldData) return oldData;

                // Handle paginated response format
                if (oldData.data && Array.isArray(oldData.data)) {
                  return {
                    ...oldData,
                    data: oldData.data.filter(
                      (item: T) => normalizeId(item.id) !== normalizedId
                    ),
                  };
                }

                // Handle array response format
                if (Array.isArray(oldData)) {
                  return oldData.filter(
                    (item: T) => normalizeId(item.id) !== normalizedId
                  );
                }

                return oldData;
              }
            );

            // Remove the entity from detail cache
            queryClient.removeQueries({
              queryKey: scopedDetailKey,
            });

            return {
              ...userCtx,
              previousData,
              queryKey: scopedDetailKey,
            };
          },

          onSuccess: (_, variables, context) => {
            // Already handled in onMutate, but ensure cache consistency
            queryClient.removeQueries({
              queryKey: detailKey(normalizeId(variables.id), variables.config),
            });

            userOnSuccess?.(_, variables, context);
          },

          onError: (error, variables, context) => {
            // Restore the entity in the cache if delete failed
            if (context?.previousData && context.queryKey) {
              queryClient.setQueryData(context.queryKey, context.previousData);

              // Restore in lists
              const normalizedId = normalizeId(variables.id);
              const previousEntity = context.previousData as T;

              queryClient.setQueriesData(
                { queryKey: keys.lists() },
                (oldData: any) => {
                  if (!oldData) return oldData;

                  // Handle paginated response format
                  if (oldData.data && Array.isArray(oldData.data)) {
                    // Only add back if it doesn't exist
                    if (
                      !oldData.data.some(
                        (item: T) => normalizeId(item.id) === normalizedId
                      )
                    ) {
                      return {
                        ...oldData,
                        data: [...oldData.data, previousEntity],
                      };
                    }
                    return oldData;
                  }

                  // Handle array response format
                  if (Array.isArray(oldData)) {
                    // Only add back if it doesn't exist
                    if (
                      !oldData.some(
                        (item: T) => normalizeId(item.id) === normalizedId
                      )
                    ) {
                      return [...oldData, previousEntity];
                    }
                    return oldData;
                  }

                  return oldData;
                }
              );
            }

            userOnError?.(error, variables, context);
          },

          onSettled: (data, error, variables, context) => {
            // Always refetch to ensure server-client consistency
            queryClient.invalidateQueries({
              queryKey: keys.lists(),
            });

            userOnSettled?.(data, error, variables, context);
          },
        });

        return {
          ...mutation,
          mutate: (id: string | number, config?: AxiosRequestConfig) => {
            mutation.mutate({ id, config });
          },
          mutateAsync: (id: string | number, config?: AxiosRequestConfig) => {
            return mutation.mutateAsync({ id, config });
          },
        };
      }
    : () => {
        throw new Error(
          `The 'useDeleteEntity' hook is disabled for '${entityName}' entity`
        );
      };

  // Return all hooks
  return {
    keys,
    useEntities,
    useEntity,
    useCreateEntity,
    useUpdateEntity,
    useDeleteEntity,
  };
}
